import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Dropdown from '../submissions-database/dropdown/Dropdown';

import { useSubmissionsContext } from '../../context/submissions';
import MultiEntryField from '../submissions-database/MultiEntryField';
import RenderActionIcons from '../submissions-database/RenderActionIcons';
import SingleEntryField from '../submissions-database/SingleEntryField';
import SubmittedByDropdown from '../submissions-database/SubmittedByDropdown';
import DataContainer from './DataContainer';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import RenderStatus from './RenderStatus';
import DocPermissionEnum from 'enums/DocPermissionEnum';

const SubmissionsTableRow = ({ item, index, isLast }) => {
  const { setSubmissionData, hiddenColumns } = useSubmissionsContext();
  const navigate = useNavigate();
  const status = item?.status;
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [currentDropdown, setCurrentDropdown] = useState({ index: null, label: '' });
  const [editingField, setEditingField] = useState({
    field: '',
    itemId: ''
  });

  const handleRowClick = (e) => {
    if (e.target.closest('button, a, input, select, textarea, [role="listbox"], [role="option"]')) return;
    navigate(`/report/${item._id}`);
  };

  const isHidden = (label) => {
    return hiddenColumns?.includes(label);
  };
  const isProcessingOrFailed = ['processing', 'failed'].includes(status?.toLowerCase());
  const canUserEdit = item?.actions?.includes(DocPermissionEnum.DOC_EDIT);
  return (
    <tr
      onClick={handleRowClick}
      className={`hover:bg-[rgba(254,102,41,0.06)] cursor-pointer font-[Arial] transition-all duration-150 ease-in-out ${!isLast && 'border-b'} border-[#1D2939] text-start`}>
      <DataContainer className="!min-w-0 !pl-2 !px-1 py-2 ">
        {!isHidden('Title') && (
          <Dropdown
            item={item}
            values={['FILM', 'TV', 'BOOK']}
            defaultValue={item?.type}
            dropdownClass="!w-28"
            title="Type"
            label="type"
            currentDropdown={currentDropdown}
            setCurrentDropdown={setCurrentDropdown}
            isSubmissionType
            index={index}
            canEdit={canUserEdit}
          />
        )}
      </DataContainer>
      {!isHidden('Title') && (
        <DataContainer colSpan={isProcessingOrFailed ? 9 : 1}>
          <div className="flex items-center gap-3">
            <SingleEntryField
              defaultValue={item.title}
              fieldName={'title'}
              item={item}
              editingField={editingField}
              setEditingField={setEditingField}
              isProcessingOrFailed={isProcessingOrFailed}
              fieldWidth="168px"
              canEdit={canUserEdit}
            />
            {status?.toLowerCase() === 'processing' && (
              <Link
                to={`/report/${item._id}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-orange-500 bg-orange-500/20 border border-orange-400/60 rounded-full hover:bg-orange-500/35 hover:border-orange-300/90 hover:text-orange-200 whitespace-nowrap transition-all duration-150">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.2"/>
                  <circle cx="5" cy="5" r="1.5" fill="currentColor" className="animate-pulse"/>
                </svg>
                View Progress
              </Link>
            )}
          </div>
        </DataContainer>
      )}
      {!isHidden('Writers/Directors/Producers') && !isProcessingOrFailed && (
        <DataContainer className={`text-nowrap`}>
          <MultiEntryField
            fieldNames={['writer', 'directors', 'producers']}
            item={item}
            editingField={editingField}
            setEditingField={setEditingField}
            fieldWidth="168px"
            canEdit={canUserEdit}
          />
        </DataContainer>
      )}
      {!isHidden('Submitted by') && !isProcessingOrFailed && (
        <DataContainer>
          <div className="flex items-center gap-2">
            <SubmittedByDropdown
              item={item}
              currentDropdown={currentDropdown}
              setCurrentDropdown={setCurrentDropdown}
              canEdit={canUserEdit}
            />
          </div>
        </DataContainer>
      )}
      {!isHidden('Genres') && !isProcessingOrFailed && (
        <DataContainer>
          <Dropdown
            item={item}
            values={[
              'Arthouse',
              'Biopic',
              'Slasher',
              'Paranormal',
              'Found Footage',
              'Psychological',
              'Dark Comedy',
              'Slapstick',
              'Heist',
              'Thriller',
              'Musical',
              'Non-Fiction',
              'Educational',
              'Technical',
              'Action',
              'Adventure',
              'Comedy',
              'Drama',
              'Horror',
              'Romance',
              'Science Fiction',
              'Fantasy',
              'Documentary',
              'Film Noir',
              'Western',
              'Historical',
              'Crime'
            ]}
            defaultValue={item?.genre}
            dropdownClass="!w-36"
            title="Genre"
            label="genre"
            currentDropdown={currentDropdown}
            setCurrentDropdown={setCurrentDropdown}
            isMultiEntry
            isSubmissionType
            index={index}
            canEdit={canUserEdit}
          />
        </DataContainer>
      )}
      {!isHidden('Logline') && !isProcessingOrFailed && (
        <DataContainer>
          <SingleEntryField
            defaultValue={item?.logline}
            fieldName={'logline'}
            textLength={10}
            lineBreakLength={30}
            item={item}
            editingField={editingField}
            setEditingField={setEditingField}
            isTextArea
            canEdit={canUserEdit}
          />
        </DataContainer>
      )}
      {!isHidden('Note') && !isProcessingOrFailed && (
        <DataContainer>
          <SingleEntryField
            defaultValue={item?.note}
            fieldName={'note'}
            textLength={10}
            lineBreakLength={30}
            item={item}
            editingField={editingField}
            setEditingField={setEditingField}
            isTextArea
            canEdit={canUserEdit}
          />
        </DataContainer>
      )}
      {!isHidden('Based on') && !isProcessingOrFailed && (
        <DataContainer>
          <SingleEntryField
            defaultValue={item?.note}
            fieldName={'based_on'}
            textLength={10}
            lineBreakLength={30}
            item={item}
            editingField={editingField}
            setEditingField={setEditingField}
            isTextArea
            canEdit={canUserEdit}
          />
        </DataContainer>
      )}
      {!isHidden('Read') && !isProcessingOrFailed && (
        <DataContainer>
          <Dropdown
            item={item}
            values={['Unread', 'Read']}
            defaultValue={item?.read ? 'Read' : 'Unread'}
            containerClass="w-20"
            title="Read"
            label="read"
            isBoolean
            currentDropdown={currentDropdown}
            setCurrentDropdown={setCurrentDropdown}
            index={index}
            canEdit={canUserEdit}
          />
        </DataContainer>
      )}
      {!isHidden('Responded') && !isProcessingOrFailed && (
        <DataContainer>
          <Dropdown
            item={item}
            values={['No Response', 'Responded']}
            defaultValue={item?.response_status ? 'Responded' : 'No Response'}
            containerClass="w-28"
            title="Response Status"
            label="response_status"
            isBoolean
            currentDropdown={currentDropdown}
            setCurrentDropdown={setCurrentDropdown}
            index={index}
            canEdit={canUserEdit}
          />
        </DataContainer>
      )}
      {!isHidden('Priority') && !isProcessingOrFailed && (
        <DataContainer className={``}>
          <Dropdown
            item={item}
            values={['Project Drafts', 'Project Samples', 'Available Material', 'General']}
            defaultValue={item?.priority || 'General'}
            onValueChange={() => {}}
            containerClass="w-24"
            title="Priority"
            label="priority"
            currentDropdown={currentDropdown}
            setCurrentDropdown={setCurrentDropdown}
            index={index}
            canEdit={canUserEdit}
          />
        </DataContainer>
      )}
      {!isHidden('Date') && (
        <DataContainer className={`px-2`} colSpan={isProcessingOrFailed ? 2 : 1}>
          <RenderStatus item={item} />
        </DataContainer>
      )}
      {!isProcessingOrFailed && (
        <DataContainer className={`!w-[1%] pr-4`}>
          <div className="flex items-center gap-2">
            <RenderActionIcons
              item={item}
              setSubmissionData={setSubmissionData}
              setConfirmationModal={setConfirmationModal}
              canEdit={canUserEdit}
            />
            <DeleteConfirmationModal
              item={item}
              confirmationModal={confirmationModal}
              setConfirmationModal={setConfirmationModal}
            />
          </div>
        </DataContainer>
      )}
    </tr>
  );
};

export default SubmissionsTableRow;
